/**
 * FriendsDataContext - Global context for friends data
 * Provides friends data to all pages in the app
 */

import React, { createContext, useContext, useCallback, useMemo, useRef } from 'react';
import { usePathname } from 'expo-router';
import { useSupabaseAuth } from '../src/shared/contexts/SupabaseAuthContext';
import { useQueryClient } from '@tanstack/react-query';
import { USER_CONFIG, MONKEY_USER_ID } from '../constants';
import { useFriendsQuery } from '../src/friends/hooks/useFriendsQuery';
import { deduplicateFriendsByEmail } from '../src/friends/helpers/friendsDeduplication.helper';
import type { Friend } from '../src/friends/types/friends.types';

interface FriendsDataContextValue {
  // Core data
  friends: Friend[];
  loading: boolean;
  error: Error | null;

  // Actions
  refreshFriends: () => Promise<void>;
  updateFriend: (friendId: string, updates: Partial<Friend>) => void;
  removeFriendFromList: (friendId: string) => void;

  // Helpers
  getFriendById: (id: string) => Friend | undefined;
  getFriendByEmail: (email: string) => Friend | undefined;
}

const FriendsDataContext = createContext<FriendsDataContextValue | undefined>(undefined);

interface FriendsDataProviderProps {
  children: React.ReactNode;
  /** When set (e.g. Storybook), use this list instead of the API. Enables invite flows without MSW. */
  mockFriends?: Friend[];
}

export const FriendsDataProvider: React.FC<FriendsDataProviderProps> = ({ children, mockFriends }) => {
  const { userId: authUserId, isSignedIn, isLoaded } = useSupabaseAuth();
  const queryClient = useQueryClient();
  const pathname = usePathname();

  // Never fall back to defaultUserId once auth says the user is signed in.
  // Otherwise requests can hit /users/friends/<default-id> with a JWT for a different user.
  const currentUserId = isSignedIn ? (authUserId ?? '') : USER_CONFIG.defaultUserId;

  // Latch: once the user visits a route that needs friends, stay enabled forever
  // so cached data persists when navigating back to Arena.
  const hasNeededFriendsRef = useRef(false);
  const needsFriends =
    pathname.startsWith('/app/friends') ||
    pathname.startsWith('/app/create') ||
    pathname.startsWith('/app/worldcup');
  if (needsFriends) hasNeededFriendsRef.current = true;

  const shouldFetchFriends =
    mockFriends == null
    && isSignedIn === true
    && isLoaded === true
    && hasNeededFriendsRef.current
    && !!authUserId;

  const {
    data: rawFriends = [],
    isLoading,
    isFetched,
    error: queryError,
    refetch,
  } = useFriendsQuery(
    currentUserId,
    shouldFetchFriends,
  );

  const sourceFriends = mockFriends ?? rawFriends;

  // Use shared deduplication helper

  /**
   * Check if a name is invalid (URL, too long, etc.)
   */
  const isInvalidName = (name: string | undefined): boolean => {
    if (!name) return true;
    if (name.startsWith('http://') || name.startsWith('https://')) return true;
    if (name.length > 80) return true;
    return false;
  };

  /**
   * Filter out friends with invalid names (URLs, etc.).
   * Always keep Monkey (system friend) so he always appears when in the API response.
   */
  const filterInvalidFriends = useCallback((friends: Friend[]): Friend[] => {
    return friends.filter(
      (friend) => friend.id === MONKEY_USER_ID || !isInvalidName(friend.name),
    );
  }, []);

  // Process friends data: deduplicate and filter invalid entries
  const friends = useMemo(() => {
    const deduplicated = deduplicateFriendsByEmail(sourceFriends);
    return filterInvalidFriends(deduplicated);
  }, [sourceFriends, filterInvalidFriends]);

  // Refresh friends - invalidate React Query cache to trigger refetch
  const refreshFriends = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['friends', currentUserId] });
    await refetch();
  }, [queryClient, currentUserId, refetch]);

  // Convert query error to Error type for compatibility
  const error = useMemo(() => {
    return queryError ? (queryError instanceof Error ? queryError : new Error(String(queryError))) : null;
  }, [queryError]);
  const loading = mockFriends != null ? false : shouldFetchFriends && (!isFetched || isLoading);

  // Update a specific friend in the list
  const updateFriend = useCallback(
    (friendId: string, updates: Partial<Friend>) => {
      // Update React Query cache
      queryClient.setQueryData<Friend[]>(['friends', currentUserId], (oldData: Friend[] | undefined) => {
        if (!oldData) return oldData;
        return oldData.map((friend: Friend) => (friend.id === friendId ? { ...friend, ...updates } : friend));
      });
    },
    [queryClient, currentUserId]
  );

  // Remove a friend from the list
  const removeFriendFromList = useCallback(
    (friendId: string) => {
      // Update React Query cache
      queryClient.setQueryData<Friend[]>(['friends', currentUserId], (oldData: Friend[] | undefined) => {
        if (!oldData) return oldData;
        return oldData.filter((friend: Friend) => friend.id !== friendId);
      });
    },
    [queryClient, currentUserId]
  );

  // Get friend by ID
  const getFriendById = useCallback(
    (id: string) => {
      return friends.find((friend) => friend.id === id);
    },
    [friends]
  );

  // Get friend by email
  const getFriendByEmail = useCallback(
    (email: string) => {
      return friends.find((friend) => friend.email?.toLowerCase() === email.toLowerCase());
    },
    [friends]
  );

  const value = useMemo(
    () => ({
      friends,
      loading,
      error,
      refreshFriends,
      updateFriend,
      removeFriendFromList,
      getFriendById,
      getFriendByEmail,
    }),
    [friends, loading, error, refreshFriends, updateFriend, removeFriendFromList, getFriendById, getFriendByEmail]
  );

  return <FriendsDataContext.Provider value={value}>{children}</FriendsDataContext.Provider>;
};

export const useFriendsData = (): FriendsDataContextValue => {
  const context = useContext(FriendsDataContext);
  if (context === undefined) {
    throw new Error('useFriendsData must be used within a FriendsDataProvider');
  }
  return context;
};
